import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { Checkbox } from '@multaqa/ui/components/ui/checkbox'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import { Input } from '@multaqa/ui/components/ui/input'
import { Label } from '@multaqa/ui/components/ui/label'
import { type FormEvent, useState } from 'react'
import { CatalogItemPicker } from '@/components/catalog-item-picker'
import { CompanyCurrencySelect } from '@/components/company-currency-select'
import { formatErrorMessage } from '@/lib/error-message'
import { type SupplierProductLink, useSaveSupplierProduct } from '../supplier-products-api'
import { OptionalTag } from './optional-tag'

/**
 * Add or edit one product a supplier sells (WP06). Editing keeps the product
 * fixed: the link is one supplier and one variant, so a different product is
 * a different link.
 */
export function SupplierProductDialog({
  companyId,
  supplierId,
  currency,
  link,
  onClose,
}: {
  companyId: string
  supplierId: string
  /** The supplier's own currency, offered as the price's currency. */
  currency: string
  link: SupplierProductLink | null
  onClose: () => void
}) {
  const { t } = useTranslation('products')
  const save = useSaveSupplierProduct(companyId, supplierId)
  const [variantId, setVariantId] = useState(link?.variant_id ?? '')
  const [supplierSku, setSupplierSku] = useState(link?.supplier_sku ?? '')
  const [unitPrice, setUnitPrice] = useState(link?.unit_price ?? '')
  const [priceCurrency, setPriceCurrency] = useState(link?.currency ?? currency)
  const [leadTime, setLeadTime] = useState(
    link?.lead_time_days === null || link?.lead_time_days === undefined
      ? ''
      : String(link.lead_time_days),
  )
  const [preferred, setPreferred] = useState(link?.is_preferred ?? false)
  const [error, setError] = useState<string | null>(null)
  const priced = unitPrice.trim() !== ''
  const incomplete = variantId === '' || (priced && priceCurrency.trim().length !== 3)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setError(null)
    save.mutate(
      {
        variantId,
        body: {
          supplier_sku: supplierSku.trim() || null,
          unit_price: priced ? Number(unitPrice) : null,
          currency: priced ? priceCurrency.trim().toUpperCase() : null,
          lead_time_days: leadTime.trim() === '' ? null : Number(leadTime),
          is_preferred: preferred,
        },
      },
      {
        onSuccess: onClose,
        onError: (failure) => setError(formatErrorMessage(failure, t('supplierLinks.saveFailed'))),
      },
    )
  }

  return (
    <Dialog open onOpenChange={(next) => (next ? undefined : onClose())}>
      <DialogContent closeLabel={t('common:action.cancel')} data-testid="supplier-product-dialog">
        <form onSubmit={submit} noValidate>
          <DialogHeader>
            <DialogTitle>
              {t(link ? 'supplierLinks.editTitle' : 'supplierLinks.addTitle')}
            </DialogTitle>
            <DialogDescription>{t('supplierLinks.dialogDescription')}</DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <div className="grid gap-1.5">
              <Label>{t('supplierLinks.product')}</Label>
              {link ? (
                <span className="tabular-nums" data-testid="supplier-product-fixed">
                  <bdi dir="ltr">{link.variant?.sku}</bdi>
                </span>
              ) : (
                <CatalogItemPicker
                  companyId={companyId}
                  value={variantId}
                  price="purchase"
                  aria-label={t('supplierLinks.product')}
                  data-testid="supplier-product-item"
                  onSelect={(variant) => setVariantId(variant.id)}
                />
              )}
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="grid gap-1.5">
                <Label htmlFor="supplier-product-sku">
                  {t('supplierLinks.supplierSku')}
                  <OptionalTag />
                </Label>
                <Input
                  id="supplier-product-sku"
                  value={supplierSku}
                  maxLength={100}
                  onChange={(event) => setSupplierSku(event.target.value)}
                  data-testid="supplier-product-sku"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="supplier-product-lead">
                  {t('supplierLinks.leadTime')}
                  <OptionalTag />
                </Label>
                <Input
                  id="supplier-product-lead"
                  type="number"
                  min={0}
                  step={1}
                  value={leadTime}
                  onChange={(event) => setLeadTime(event.target.value)}
                  className="text-end tabular-nums"
                  data-testid="supplier-product-lead"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="supplier-product-price">
                  {t('supplierLinks.price')}
                  <OptionalTag />
                </Label>
                <Input
                  id="supplier-product-price"
                  type="number"
                  min={0}
                  step="any"
                  value={unitPrice}
                  onChange={(event) => setUnitPrice(event.target.value)}
                  className="text-end tabular-nums"
                  data-testid="supplier-product-price"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="supplier-product-currency">
                  {t('supplierLinks.currency')}
                  <OptionalTag />
                </Label>
                <CompanyCurrencySelect
                  id="supplier-product-currency"
                  value={priceCurrency}
                  onValueChange={setPriceCurrency}
                  testId="supplier-product-currency"
                  className="w-full"
                />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="supplier-product-preferred"
                checked={preferred}
                onCheckedChange={(checked) => setPreferred(checked === true)}
                data-testid="supplier-product-preferred"
              />
              <Label htmlFor="supplier-product-preferred">{t('supplierLinks.preferredHint')}</Label>
            </div>
            {error ? (
              <p
                className="text-danger text-small"
                role="alert"
                data-testid="supplier-product-error"
              >
                {error}
              </p>
            ) : null}
          </DialogBody>
          <DialogFooter showCloseButton={false} closeLabel={t('common:action.cancel')}>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('common:action.cancel')}
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={save.isPending}
              disabled={incomplete || save.isPending}
              data-testid="supplier-product-save"
            >
              {t('supplierLinks.save')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
